import React, { useCallback, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import PropertyMap from "./PropertyMap";

const BLUE = "#00336C";
const GREEN = "#648315";
const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const number = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const id = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
const textValue = (value) => value == null || value === "" ? "—" : value;

const CONFIG = {
  contacts: {
    label: "Contacts",
    singular: "contact",
    empty: "No property contacts have been added.",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "role", label: "Role / relationship" },
      { name: "company", label: "Company" },
      { name: "email", label: "Email", type: "email" },
      { name: "phone", label: "Phone" },
    ],
    columns: [
      ["name", "Name"], ["role", "Role"], ["company", "Company"], ["email", "Email"], ["phone", "Phone"],
    ],
  },
  documents: {
    label: "Documents",
    singular: "document",
    empty: "No property documents have been linked.",
    fields: [
      { name: "name", label: "Document name", required: true },
      { name: "category", label: "Category", select: ["Deed", "Inspection", "Site plan", "Tax record", "Lease", "Other"] },
      { name: "url", label: "Document URL", type: "url", required: true },
      { name: "description", label: "Description", multiline: true },
    ],
    columns: [["name", "Name"], ["category", "Category"], ["description", "Description"]],
  },
  notes: {
    label: "Notes",
    singular: "note",
    empty: "No notes have been recorded.",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "body", label: "Note", required: true, multiline: true },
    ],
    columns: [["title", "Title"], ["body", "Note"], ["createdAt", "Created"]],
  },
  occupancyRecords: {
    label: "Occupancy",
    singular: "occupancy record",
    empty: "No occupancy records have been added.",
    fields: [
      { name: "tenant", label: "Tenant / occupant", required: true },
      { name: "squareFeet", label: "Occupied square feet", type: "number", required: true },
      { name: "startDate", label: "Start date", type: "date" },
      { name: "endDate", label: "End date", type: "date" },
      { name: "status", label: "Status", select: ["Active", "Scheduled", "Vacated"], required: true },
    ],
    columns: [["tenant", "Tenant"], ["squareFeet", "Sq. ft."], ["startDate", "Start"], ["endDate", "End"], ["status", "Status"]],
  },
  leases: {
    label: "Leases",
    singular: "lease",
    empty: "No leases have been added.",
    fields: [
      { name: "tenant", label: "Tenant", required: true },
      { name: "startDate", label: "Start date", type: "date", required: true },
      { name: "endDate", label: "End date", type: "date", required: true },
      { name: "monthlyRent", label: "Monthly rent", type: "number", required: true },
      { name: "status", label: "Status", select: ["Draft", "Active", "Expiring", "Expired", "Terminated"], required: true },
      { name: "notes", label: "Terms / notes", multiline: true },
    ],
    columns: [["tenant", "Tenant"], ["startDate", "Start"], ["endDate", "End"], ["monthlyRent", "Monthly rent"], ["status", "Status"]],
  },
};

function RecordDialog({ config, initial, open, onClose, onSave }) {
  const [form, setForm] = useState(initial || {});
  React.useEffect(() => setForm(initial || {}), [initial, open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box component="form" onSubmit={(event) => { event.preventDefault(); onSave(form); }}>
        <DialogTitle sx={{ color: BLUE, fontWeight: 800 }}>
          {initial?.id ? "Edit" : "Add"} {config.singular}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important", display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          {config.fields.map((field) => (
            <TextField
              key={field.name}
              required={field.required}
              select={Boolean(field.select)}
              type={field.type || "text"}
              label={field.label}
              value={form[field.name] ?? ""}
              onChange={(event) => setForm((current) => ({ ...current, [field.name]: event.target.value }))}
              multiline={field.multiline}
              minRows={field.multiline ? 3 : undefined}
              inputProps={field.type === "number" ? { min: 0, step: "any" } : undefined}
              InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
              sx={{ gridColumn: field.multiline || field.name === "url" ? "1 / -1" : undefined }}
            >
              {(field.select || []).map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
            </TextField>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button type="submit" variant="contained" sx={{ bgcolor: BLUE, textTransform: "none", fontWeight: 700 }}>Save</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function RecordsPanel({ configKey, records, onChange }) {
  const config = CONFIG[configKey];
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const save = (record) => {
    const next = { ...record, id: record.id || id() };
    if (configKey === "notes" && !next.createdAt) next.createdAt = new Date().toISOString().slice(0, 10);
    onChange(editing ? records.map((item) => item.id === editing.id ? next : item) : [next, ...records]);
    setOpen(false);
  };
  const display = (field, value, record) => {
    if (field === "squareFeet") return value === "" || value == null ? "—" : number.format(Number(value));
    if (field === "monthlyRent") return value === "" || value == null ? "—" : money.format(Number(value));
    if (field === "name" && configKey === "documents" && record.url) {
      return <Button href={record.url} target="_blank" rel="noreferrer" endIcon={<OpenInNewIcon />} sx={{ p: 0, minWidth: 0, textTransform: "none", justifyContent: "flex-start" }}>{value}</Button>;
    }
    return textValue(value);
  };
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${alpha(BLUE, .12)}`, borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${alpha(BLUE, .09)}` }}>
        <Box>
          <Typography variant="h6" sx={{ color: BLUE, fontWeight: 800 }}>{config.label}</Typography>
          <Typography variant="body2" color="text.secondary">{records.length} {records.length === 1 ? config.singular : `${config.singular}s`}</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setOpen(true); }} sx={{ bgcolor: GREEN, textTransform: "none", fontWeight: 700 }}>Add {config.singular}</Button>
      </Box>
      {!records.length ? (
        <Typography color="text.secondary" sx={{ p: 5, textAlign: "center" }}>{config.empty}</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", "& th, & td": { px: 2, py: 1.5, textAlign: "left", borderBottom: `1px solid ${alpha(BLUE, .08)}`, whiteSpace: "nowrap" }, "& th": { color: BLUE, fontSize: 13, bgcolor: alpha(BLUE, .025) } }}>
            <thead><tr>{config.columns.map(([, label]) => <th key={label}>{label}</th>)}<th aria-label="Actions" /></tr></thead>
            <tbody>{records.map((record) => (
              <tr key={record.id}>
                {config.columns.map(([field]) => <td key={field}>{display(field, record[field], record)}</td>)}
                <td>
                  <IconButton aria-label={`Edit ${config.singular}`} size="small" onClick={() => { setEditing(record); setOpen(true); }}><EditOutlinedIcon fontSize="small" /></IconButton>
                  <IconButton aria-label={`Delete ${config.singular}`} size="small" color="error" onClick={() => onChange(records.filter((item) => item.id !== record.id))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </td>
              </tr>
            ))}</tbody>
          </Box>
        </Box>
      )}
      <RecordDialog config={config} initial={editing} open={open} onClose={() => setOpen(false)} onSave={save} />
    </Paper>
  );
}

function FinancialPanel({ value, onChange }) {
  const fields = [
    ["assessedValue", "Assessed value"], ["purchasePrice", "Purchase price"],
    ["annualRevenue", "Annual revenue"], ["annualExpenses", "Annual operating expenses"],
    ["annualTaxes", "Annual property taxes"], ["capitalExpenditures", "Capital expenditures"],
  ];
  const net = Number(value.annualRevenue || 0) - Number(value.annualExpenses || 0) - Number(value.annualTaxes || 0);
  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${alpha(BLUE, .12)}`, borderRadius: 3 }}>
      <Typography variant="h6" sx={{ color: BLUE, fontWeight: 800 }}>Financial information</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>Track valuation, income, expenses, and taxes.</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" }, gap: 2 }}>
        {fields.map(([field, label]) => <TextField key={field} label={label} type="number" value={value[field] ?? ""} onChange={(event) => onChange({ ...value, [field]: event.target.value })} inputProps={{ min: 0, step: "any" }} />)}
      </Box>
      <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(net >= 0 ? GREEN : "#d32f2f", .08) }}>
        <Typography variant="caption" color="text.secondary">Estimated annual net operating income</Typography>
        <Typography variant="h5" sx={{ color: net >= 0 ? GREEN : "error.main", fontWeight: 800 }}>{money.format(net)}</Typography>
      </Box>
    </Paper>
  );
}

function AnalyticsPanel({ property }) {
  const activeOccupancy = property.occupancyRecords.filter((record) => record.status === "Active");
  const occupied = activeOccupancy.reduce((sum, record) => sum + Number(record.squareFeet || 0), 0);
  const total = Number(property.squareFeet || 0);
  const occupancyRate = total ? Math.min(100, occupied / total * 100) : 0;
  const activeLeases = property.leases.filter((lease) => lease.status === "Active" || lease.status === "Expiring");
  const annualRent = activeLeases.reduce((sum, lease) => sum + Number(lease.monthlyRent || 0) * 12, 0);
  const financial = property.financialInformation;
  const noi = Number(financial.annualRevenue || 0) - Number(financial.annualExpenses || 0) - Number(financial.annualTaxes || 0);
  const chart = [
    { name: "Occupied", value: occupied },
    { name: "Available", value: Math.max(0, total - occupied) },
  ];
  const metrics = [
    ["Occupancy rate", `${number.format(occupancyRate)}%`],
    ["Occupied area", `${number.format(occupied)} sq. ft.`],
    ["Active leases", activeLeases.length],
    ["Annualized rent", money.format(annualRent)],
    ["Net operating income", money.format(noi)],
  ];
  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(5, 1fr)" }, gap: 2 }}>
        {metrics.map(([label, value]) => <Paper key={label} elevation={0} sx={{ p: 2.25, border: `1px solid ${alpha(BLUE, .12)}`, borderRadius: 2.5 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h6" sx={{ color: BLUE, fontWeight: 800 }}>{value}</Typography></Paper>)}
      </Box>
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${alpha(BLUE, .12)}`, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: BLUE, fontWeight: 800, mb: 2 }}>Space utilization</Typography>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" /><YAxis /><Tooltip formatter={(value) => `${number.format(value)} sq. ft.`} /><Bar dataKey="value" fill={GREEN} radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Box>
      </Paper>
    </Box>
  );
}

export default function PropertyWorkspace({ property, saving, onSave, activeTab, visible = true }) {
  const [tab, setTab] = useState("occupancyRecords");
  const [draft, setDraft] = useState(property);
  const changed = useMemo(() => JSON.stringify(draft) !== JSON.stringify(property), [draft, property]);
  React.useEffect(() => setDraft(property), [property]);
  React.useEffect(() => {
    if (["occupancyRecords", "leases", "financial", "map", "analytics"].includes(activeTab)) setTab(activeTab);
  }, [activeTab]);
  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const mapChange = useCallback((latitude, longitude) => {
    setDraft((current) => ({ ...current, latitude, longitude }));
  }, []);
  return (
    <Box sx={{ mt: 1, display: visible ? "block" : "none" }}>
      {CONFIG[tab] && <RecordsPanel configKey={tab} records={draft[tab] || []} onChange={(value) => update(tab, value)} />}
      {tab === "financial" && <FinancialPanel value={draft.financialInformation || {}} onChange={(value) => update("financialInformation", value)} />}
      {tab === "map" && (
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${alpha(BLUE, .12)}`, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: BLUE, fontWeight: 800 }}>Interactive property map</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Click the map or enter coordinates to place the property marker.</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "180px 180px 1fr" }, gap: 2, mb: 2 }}>
            <TextField label="Latitude" type="number" value={draft.latitude ?? ""} onChange={(event) => update("latitude", event.target.value === "" ? "" : Number(event.target.value))} inputProps={{ min: -90, max: 90, step: "any" }} />
            <TextField label="Longitude" type="number" value={draft.longitude ?? ""} onChange={(event) => update("longitude", event.target.value === "" ? "" : Number(event.target.value))} inputProps={{ min: -180, max: 180, step: "any" }} />
          </Box>
          <PropertyMap
            latitude={draft.latitude === "" || draft.latitude == null ? NaN : Number(draft.latitude)}
            longitude={draft.longitude === "" || draft.longitude == null ? NaN : Number(draft.longitude)}
            onChange={mapChange}
          />
        </Paper>
      )}
      {tab === "analytics" && <AnalyticsPanel property={draft} />}
      {changed && (
        <Paper elevation={8} sx={{ position: "sticky", bottom: 16, mt: 2, ml: "auto", p: 1.5, width: "fit-content", display: "flex", alignItems: "center", gap: 1.5, borderRadius: 2.5 }}>
          <Typography variant="body2" sx={{ color: BLUE, fontWeight: 700 }}>Unsaved property changes</Typography>
          <Button disabled={saving} onClick={() => setDraft(property)} sx={{ textTransform: "none" }}>Discard</Button>
          <Button disabled={saving} variant="contained" onClick={() => onSave(draft)} sx={{ bgcolor: GREEN, textTransform: "none", fontWeight: 700 }}>{saving ? "Saving…" : "Save changes"}</Button>
        </Paper>
      )}
    </Box>
  );
}
